import { ArrowDownLeft, ArrowUpRight, Bell, CheckCircle2, Clock3, CreditCard, Landmark, Lock, Paperclip, Pin, Repeat, StickyNote, Wallet } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { RowMenu } from '@/components/common/RowMenu'
import { EmptyState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { confirmModal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney, fmtRelative } from '../../../shared/format'
import { toastError } from '../../ui'
import { useDeleteEntry, useDeleteNote, useDeleteWallet, usePersonalEntries, usePersonalNotes, usePersonalWallets, useToggleEntry } from '../api'
import type { PersonalEntry, PersonalNote, PersonalWallet } from '../types'

const WALLET_ICON = { cash: Wallet, bank: Landmark, credit_card: CreditCard }

export function EntriesList({ kind, month, onEdit }: { kind: 'all' | 'income' | 'expense'; month: string | null; onEdit: (e: PersonalEntry) => void }) {
  const { t } = useTranslation()
  const entries = usePersonalEntries()
  const wallets = usePersonalWallets()
  const toggle = useToggleEntry()
  const remove = useDeleteEntry()
  const walletName = new Map((wallets.data ?? []).map((w) => [w.id, w.name]))
  const rows = (entries.data ?? []).filter((e) => (kind === 'all' || e.kind === kind) && (!month || e.date.startsWith(month)))

  const onDelete = async (e: PersonalEntry) => {
    if (!(await confirmModal({ title: t('finance.personal.deleteEntryTitle'), description: t('finance.personal.deleteEntryBody', { amount: fmtMoney(e.amount) }), confirmLabel: t('common.delete'), destructive: true }))) return
    remove.mutate(e.id, { onSuccess: () => toast.success(t('common.deleted')), onError: toastError })
  }

  if (entries.isLoading) return <Skeleton className="m-4 h-40" />
  if (!rows.length) return <EmptyState icon={Wallet} title={t('finance.personal.noEntries')} />
  return (
    <ul className="divide-y">
      {rows.map((e) => {
        const isIn = e.kind === 'income'
        return (
          <li key={e.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3">
            <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-full', isIn ? 'bg-success/12 text-success' : 'bg-destructive/10 text-destructive')}>
              {isIn ? <ArrowDownLeft className="size-4" aria-hidden /> : <ArrowUpRight className="size-4" aria-hidden />}
            </span>
            <div className="min-w-0 flex-1 basis-48">
              <p className="truncate text-sm font-medium">{e.description || t(`finance.personal.category.${e.category}`)}</p>
              <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                {fmtDate(e.date)} · {t(`finance.personal.category.${e.category}`)} · {walletName.get(e.walletId) ?? '—'}
                {e.recurrence !== 'once' ? (
                  <span className="inline-flex items-center gap-0.5">
                    · <Repeat className="size-3" aria-hidden /> {t(`finance.personal.recurrenceOpt.${e.recurrence}`)}
                  </span>
                ) : null}
                {e.attachmentName ? <Paperclip className="size-3" aria-label={e.attachmentName} /> : null}
              </p>
            </div>
            {e.locked ? (
              <Badge tone="primary" title={t('finance.personal.lockedHint')}>
                <Lock className="size-3" aria-hidden />
                {t('finance.personal.systemRecord')}
              </Badge>
            ) : (
              <button
                type="button"
                onClick={() => toggle.mutate(e, { onError: toastError })}
                className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('finance.personal.toggleStatus')}
                title={t('finance.personal.toggleStatus')}
              >
                <Badge tone={e.status === 'paid' ? 'success' : 'warning'}>
                  {e.status === 'paid' ? <CheckCircle2 className="size-3" aria-hidden /> : <Clock3 className="size-3" aria-hidden />}
                  {t(`finance.personal.status.${e.kind}.${e.status}`)}
                </Badge>
              </button>
            )}
            <span className={cn('w-28 text-right font-semibold tabular-nums', isIn ? 'text-success' : 'text-destructive')}>
              {isIn ? '+' : '−'}
              {fmtMoney(e.amount)}
            </span>
            {/* Maaş ve firma ödemeleri sistem kaydıdır: kişi değiştiremez/silemez. */}
            {e.locked ? <span className="size-8" aria-hidden /> : <RowMenu label={e.description || e.category} onEdit={() => onEdit(e)} onDelete={() => void onDelete(e)} />}
          </li>
        )
      })}
    </ul>
  )
}

export function WalletsGrid({ onEdit }: { onEdit: (w: PersonalWallet) => void }) {
  const { t } = useTranslation()
  const wallets = usePersonalWallets()
  const remove = useDeleteWallet()
  const onDelete = async (w: PersonalWallet) => {
    if (!(await confirmModal({ title: t('finance.personal.deleteWalletTitle'), description: t('finance.personal.deleteWalletBody', { name: w.name }), confirmLabel: t('common.delete'), destructive: true }))) return
    remove.mutate(w.id, { onSuccess: () => toast.success(t('common.deleted')), onError: toastError })
  }
  if (wallets.isLoading) return <Skeleton className="h-40" />
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {(wallets.data ?? []).map((w) => {
        const Icon = WALLET_ICON[w.type]
        const neg = w.balance.startsWith('-')
        return (
          <article key={w.id} className="flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-card">
            <div className="flex items-start gap-3">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon className="size-5" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="truncate font-semibold">{w.name}</h3>
                <p className="truncate text-xs text-muted-foreground">
                  {t(`finance.personal.walletType.${w.type}`)}
                  {w.bankName ? ` · ${w.bankName}` : ''}
                  {w.last4 ? ` · •••• ${w.last4}` : ''}
                </p>
              </div>
              <RowMenu label={w.name} onEdit={() => onEdit(w)} onDelete={() => void onDelete(w)} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t(w.type === 'credit_card' && neg ? 'finance.accounts.cardDebt' : 'finance.accounts.balance')}</p>
              <p className={cn('text-2xl font-semibold tabular-nums', neg && 'text-destructive')}>{fmtMoney(w.balance)}</p>
            </div>
          </article>
        )
      })}
    </div>
  )
}

export function NotesGrid({ onEdit, pinnedOnly }: { onEdit: (n: PersonalNote) => void; pinnedOnly?: boolean }) {
  const { t } = useTranslation()
  const notes = usePersonalNotes()
  const remove = useDeleteNote()
  const list = (notes.data ?? []).filter((n) => !pinnedOnly || n.pinned)
  const onDelete = async (n: PersonalNote) => {
    if (!(await confirmModal({ title: t('finance.personal.deleteNoteTitle'), description: t('finance.personal.deleteNoteBody', { name: n.title }), confirmLabel: t('common.delete'), destructive: true }))) return
    remove.mutate(n.id, { onSuccess: () => toast.success(t('common.deleted')), onError: toastError })
  }
  if (notes.isLoading) return <Skeleton className="h-40" />
  if (!list.length) return <EmptyState icon={StickyNote} title={t('finance.personal.noNotes')} />
  return (
    <ul className={cn('grid grid-cols-1 gap-4', !pinnedOnly && 'sm:grid-cols-2 xl:grid-cols-3')}>
      {list.map((n) => (
        <li key={n.id} className={cn('flex flex-col rounded-xl border bg-card p-4 shadow-card', n.pinned && 'border-warning/40 bg-warning/[0.05]')}>
          <div className="flex items-start gap-2">
            {n.pinned ? <Pin className="mt-0.5 size-4 shrink-0 text-warning" aria-label={t('finance.personal.form.pinned')} /> : null}
            <h3 className="min-w-0 flex-1 font-semibold">{n.title}</h3>
            <RowMenu label={n.title} onEdit={() => onEdit(n)} onDelete={() => void onDelete(n)} />
          </div>
          {n.tags.length ? (
            <div className="mt-1 flex flex-wrap gap-1">
              {n.tags.map((tag) => (
                <Badge key={tag} tone="primary" className="text-[11px]">
                  {tag}
                </Badge>
              ))}
            </div>
          ) : null}
          {n.body ? <p className="mt-2 line-clamp-4 flex-1 whitespace-pre-wrap break-words text-sm text-muted-foreground">{n.body}</p> : null}
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>{fmtRelative(n.updatedAt)}</span>
            {n.remindAt ? (
              <span className="inline-flex items-center gap-1 text-primary">
                <Bell className="size-3" aria-hidden />
                {fmtDate(n.remindAt)}
              </span>
            ) : null}
            {n.attachmentName ? (
              <span className="inline-flex items-center gap-1">
                <Paperclip className="size-3" aria-hidden />
                {n.attachmentName}
              </span>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  )
}
